import { useState, useCallback, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useHealedDetail } from "@/hooks/useHealedDetail"
import { useArtists } from "@/hooks/useArtists"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useAuthState } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Share2, MessageCircle } from "lucide-react"
import { AuthModal } from "@/components/auth/AuthModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { CollectionSelectSheet } from "@/components/shared/tattoo/CollectionSelectSheet"
import { SimilarItems } from "@/components/details/SimilarItems"
import { saveItemToCollections, getItemSaveState } from "@/lib/savesApi"
import { formatArtistDisplayName } from "@/lib/utils"

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
}

interface HealedDetailContentProps {
  healedId: string
  onBack?: () => void
  shareUrl?: string
}

export function HealedDetailContent({ healedId, onBack, shareUrl }: HealedDetailContentProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { healed, loading, error } = useHealedDetail(healedId)
  const { artists } = useArtists(healed ? [healed.artistId] : [])
  const { trackEvent } = useAnalytics()
  const { user } = useAuthState()
  
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authModalContext, setAuthModalContext] = useState<'save' | 'contact'>('contact')
  const [showShareModal, setShowShareModal] = useState(false)
  const [showCollectionSheet, setShowCollectionSheet] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  
  // Pinch-to-zoom and pan state
  const imageContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isZooming, setIsZooming] = useState(false)
  const [isPanning, setIsPanning] = useState(false)
  const initialDistanceRef = useRef<number>(0)
  const initialScaleRef = useRef<number>(1)
  const initialPositionRef = useRef({ x: 0, y: 0 })
  const lastTouchCenterRef = useRef({ x: 0, y: 0 })
  const touchStartTimeRef = useRef<number>(0)

  const artist = artists[0]

  // Scroll to top when healedId changes
  useEffect(() => {
    if (!healed) return
    
    const scrollToTop = () => {
      let scrollableElement: Element | null = null
      
      if (contentRef.current) {
        let element = contentRef.current.parentElement
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY
          if (overflowY === 'auto' || overflowY === 'scroll') {
            scrollableElement = element
            break
          }
          element = element.parentElement
        }
      }
      
      if (!scrollableElement) {
        scrollableElement = document.querySelector('[role="dialog"]')
      }
      
      if (scrollableElement) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    
    const timeoutId = setTimeout(scrollToTop, 50)
    return () => clearTimeout(timeoutId)
  }, [healedId, healed])

  useEffect(() => {
    if (healed && artist) {
      trackEvent('item_viewed', {
        item_id: healed.id,
        item_type: 'healed',
        artist_id: healed.artistId,
        source: 'healed_detail'
      })
    }
  }, [healed, artist, trackEvent])

  const handleShowArtist = () => {
    if (artist) {
      navigate(`/artist/${artist.id}`)
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigate(-1)
    }
  }

  const handleContactArtist = useCallback(async () => {
    if (!healed) return
    await trackEvent('artist_message_clicked', {
      artist_id: healed.artistId,
      source: 'healed_detail',
      item_id: healed.id,
      item_type: 'healed'
    })
    navigate(`/chats/new?artistId=${healed.artistId}&source=healed_detail`)
  }, [healed, navigate, trackEvent])

  const handleContactArtistClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!user) {
      setAuthModalContext('contact')
      setShowAuthModal(true)
      return
    }

    handleContactArtist()
  }, [user, handleContactArtist])

  const handleAuthSuccess = () => {
    if (authModalContext === 'save') {
      setShowCollectionSheet(true)
    } else {
      handleContactArtist()
    }
  }

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setShowAuthModal(open)
  }, [])

  const [saveState, setSaveState] = useState<{ isSaved: boolean, savesCount: number }>({
    isSaved: false,
    savesCount: 0
  })

  useEffect(() => {
    let mounted = true
    
    const fetchInitialState = async () => {
      if (!healed) return
      
      try {
        const state = await getItemSaveState(healed.id, user?.uid || null, false)
        if (mounted) {
          setSaveState(state)
        }
      } catch (error) {
        console.error("Error fetching initial save state:", error)
      }
    }

    fetchInitialState()
    return () => { mounted = false }
  }, [healed, user?.uid])

  const handleSaveClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!user) {
      setAuthModalContext('save')
      setShowAuthModal(true)
      return
    }
    
    if (!healed) return
    
    setShowCollectionSheet(true)
  }, [user, healed])

  const handleSaveToCollections = useCallback(async (selectedCollectionIds: string[]) => {
    if (!user || !healed) {
      console.error('[HealedDetailContent] Cannot save - missing user or healed item', { user: !!user, healed: !!healed })
      return
    }

    try {
      await saveItemToCollections(
        healed.id, 
        user.uid, 
        false,
        selectedCollectionIds
      )
      
      const isNowSaved = selectedCollectionIds.length > 0
      trackEvent('item_saved', {
        item_id: healed.id,
        item_type: 'healed',
        artist_id: healed.artistId,
        source: 'healed_detail',
        is_saved: isNowSaved
      })
      
      const newState = await getItemSaveState(healed.id, user.uid, false)
      setSaveState(newState)
    } catch (error) {
      console.error('[HealedDetailContent] Error saving to collections:', error)
    }
  }, [user, healed, trackEvent])

  const handleShare = () => {
    const currentUrl = shareUrl || window.location.href

    if (isMobileDevice() && navigator.share) {
      navigator.share({
        title: `Healed tattoo by ${formatArtistDisplayName(artist) || 'Artist'}`,
        text: `Check out this healed tattoo!`,
        url: currentUrl,
      })
        .then(() => {
          if (!healed) return
          trackEvent('item_shared', {
            item_id: healed.id,
            item_type: 'healed',
            artist_id: healed.artistId,
            source: 'healed_detail',
            share_method: 'native'
          })
        })
        .catch(console.error)
    } else {
      setShowShareModal(true)
    }
  }

  const handleAvatarClick = () => {
    handleShowArtist()
  }

  // Pinch-to-zoom and pan handlers
  const getTouchDistance = (touches: React.TouchList) => {
    const touch1 = touches[0]
    const touch2 = touches[1]
    const dx = touch2.clientX - touch1.clientX
    const dy = touch2.clientY - touch1.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  const getTouchCenter = (touches: React.TouchList) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY }
    }
    const touch1 = touches[0]
    const touch2 = touches[1]
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    }
  }

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartTimeRef.current = Date.now()
    
    if (e.touches.length === 2) {
      e.preventDefault()
      setIsZooming(true)
      setIsPanning(false)
      initialDistanceRef.current = getTouchDistance(e.touches)
      initialScaleRef.current = scale
      initialPositionRef.current = position
      lastTouchCenterRef.current = getTouchCenter(e.touches)
    } else if (e.touches.length === 1 && scale > 1) {
      e.preventDefault()
      setIsPanning(true)
      setIsZooming(false)
      initialPositionRef.current = position
      lastTouchCenterRef.current = getTouchCenter(e.touches)
    }
  }, [scale, position])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && isZooming) {
      e.preventDefault()
      
      const currentDistance = getTouchDistance(e.touches)
      const scaleFactor = currentDistance / initialDistanceRef.current
      const newScale = Math.max(1, Math.min(4, initialScaleRef.current * scaleFactor))
      
      const currentCenter = getTouchCenter(e.touches)
      const deltaX = currentCenter.x - lastTouchCenterRef.current.x
      const deltaY = currentCenter.y - lastTouchCenterRef.current.y
      
      setScale(newScale)
      setPosition({
        x: initialPositionRef.current.x + deltaX,
        y: initialPositionRef.current.y + deltaY
      })
    } else if (e.touches.length === 1 && isPanning && scale > 1) {
      e.preventDefault()
      
      const currentCenter = getTouchCenter(e.touches)
      const deltaX = currentCenter.x - lastTouchCenterRef.current.x
      const deltaY = currentCenter.y - lastTouchCenterRef.current.y
      
      setPosition({
        x: initialPositionRef.current.x + deltaX,
        y: initialPositionRef.current.y + deltaY
      })
    }
  }, [isZooming, isPanning, scale])

  const handleTouchEnd = useCallback(() => {
    const touchDuration = Date.now() - touchStartTimeRef.current
    
    if (isZooming || (isPanning && touchDuration < 100)) {
      setScale(1)
      setPosition({ x: 0, y: 0 })
    }
    
    setIsZooming(false)
    setIsPanning(false)
  }, [isZooming, isPanning])

  if (loading) {
    return (
      <div className="bg-background">
        <div className="phone-container-padded py-4">
          <div className="flex items-center mb-6">
            <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
          </div>

          <div className="mb-12 flex justify-center relative">
            <div className="relative max-w-screen-lg mx-auto rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full">
              <div 
                className="w-full bg-muted animate-pulse"
                style={{ aspectRatio: '1/1.4' }}
              />
            </div>

            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
              <div className="h-14 w-48 bg-muted rounded-lg animate-pulse" />
            </div>
          </div>

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              <div>
                <div className="h-5 w-32 bg-muted rounded animate-pulse mb-2" />
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !healed) {
    return (
      <div className="bg-background">
        <div className="phone-container-padded py-4">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">{t('common.error')}</h1>
          </div>
          <div className="text-center py-16">
            <p className="text-muted-foreground">{t('common.tattooNotFound')}</p>
            <Button onClick={handleBack} className="mt-4">
              {t('common.goBack')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div ref={contentRef} className="bg-background">
        <div className="phone-container-padded py-4">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </div>

          {/* Healed Image */}
          <div className="mb-12 flex justify-center relative">
            <div 
              ref={imageContainerRef}
              className="relative max-w-screen-lg mx-auto rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                touchAction: 'none'
              }}
            >
              {!imageLoaded && (
                <div 
                  className="w-full bg-muted animate-pulse"
                  style={{ aspectRatio: healed.width && healed.height ? `${healed.width}/${healed.height}` : '1/1' }}
                />
              )}
              <img
                src={healed.imageUrl}
                alt={healed.caption || t('common.tattooDesign')}
                className={`w-full h-auto object-cover lg:max-h-[60vh] lg:w-auto transition-all duration-300 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
                }`}
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                  transition: isZooming || isPanning ? 'none' : 'transform 0.3s ease-out',
                  transformOrigin: 'center center'
                }}
                onLoad={() => setImageLoaded(true)}
              />
            </div>

            {imageLoaded && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
                <div 
                  onClick={handleContactArtistClick}
                  className="text-flash-reservation font-medium px-6 py-4 rounded-lg transition-colors shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                >
                  <MessageCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{t('common.contactArtist')}</span>
                </div>
              </div>
            )}
          </div>

          {/* Info and Action Row */}
          <div className="flex items-center justify-between mb-8">
            {imageLoaded ? (
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-75 transition-opacity animate-in fade-in-0 slide-in-from-left-4 duration-300"
                onClick={handleAvatarClick}
              >
                {artist?.avatarUrl && (
                  <img
                    src={artist.avatarUrl}
                    alt={formatArtistDisplayName(artist) || artist.displayName}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <div className="font-medium text-foreground">
                    {formatArtistDisplayName(artist) || t('common.artistName')}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t('common.healedWork')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
                <div>
                  <div className="h-5 w-32 bg-muted rounded animate-pulse mb-2" />
                  <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                </div>
              </div>
            )}

            {imageLoaded ? (
              <div className="flex items-center gap-3 animate-in fade-in-0 slide-in-from-right-4 duration-300">
                <button 
                  onClick={handleShare}
                  className="bg-primary text-primary-foreground w-12 h-12 rounded-lg flex items-center justify-center transition-colors hover:bg-primary/90"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleSaveClick}
                  className="bg-primary text-primary-foreground w-12 h-12 rounded-lg flex items-center justify-center transition-colors hover:bg-primary/90"
                >
                  {saveState.isSaved ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
                    </svg>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              </div>
            )}
          </div>

          {/* Similar Items Section */}
          <SimilarItems itemId={healed.id} itemType="healedPortfolioItem" fallbackArtistId={healed.artistId} />
        </div>
      </div>
      
      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={handleAuthModalOpenChange}
        onSuccess={handleAuthSuccess}
        source="healed_detail_page"
        defaultMode="signup"
      />
      
      <ShareModal
        isOpen={showShareModal}
        onOpenChange={setShowShareModal}
        url={shareUrl || window.location.href}
        title={`Healed tattoo by ${formatArtistDisplayName(artist) || 'Artist'}`}
        description="Check out this healed tattoo!"
        imageUrl={healed.imageUrl}
        itemId={healed.id}
        itemType="healed"
        artistId={healed.artistId}
        source="healed_detail"
      />

      {healed && (
        <CollectionSelectSheet
          isOpen={showCollectionSheet}
          onOpenChange={setShowCollectionSheet}
          item={healed}
          onSave={handleSaveToCollections}
        />
      )}
    </>
  )
}
